<div class="fixed bg-gray-100 inset-0 -z-10"></div>

<div class="flex min-h-full flex-col justify-center py-24 sm:px-6 lg:px-8">
  <div class="sm:mx-auto sm:w-full sm:max-w-md">
    <div class="icon-background h-32 w-32 rounded-full mx-auto grid place-content-center">
      <img alt="Nymu5 Club Management Logo" class="mx-auto h-24 w-auto" src="./assets/images/icon_nobackground_md.png">
    </div>
    <h2 class="mt-6 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">Login-Bereich</h2>
  </div>

  <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
    <div class="bg-white shadow sm:rounded-lg">
      <!--
        Second sign-in step (#282). Replaces the whole card rather than appearing beneath the form: the password
        has already been accepted and re-showing it invites somebody to retype it, which starts a NEW sign-in
        and invalidates the challenge they are in the middle of answering.
      -->
      @if (step() === 'twoFactor') {
        <div class="mx-6 space-y-6 py-12 sm:mx-12">
          <div class="space-y-2">
            <h3 class="text-lg font-semibold text-gray-900">Bestätigung in zwei Schritten</h3>
            <p class="text-sm text-gray-600">
              @if (usingRecoveryCode()) {
                Geben Sie einen Ihrer Wiederherstellungscodes ein. Jeder Code funktioniert genau einmal.
              } @else {
                Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein.
              }
            </p>
          </div>

          @if (twoFactorError(); as message) {
            <p role="alert" class="rounded-md bg-red-50 p-3 text-sm text-red-700">{{ message }}</p>
          }

          @if (usingRecoveryCode()) {
            <div>
              <label class="block text-sm font-medium leading-6 text-gray-900" for="recovery-code">
                Wiederherstellungscode
              </label>
              <div class="mt-2">
                <!-- ONE listener that assigns first: `[(ngModel)]` desugars into `[ngModel]` + an assigning
                     `(ngModelChange)`, so a second handler beside it would read the previous value
                     (cheqme/no-ngmodelchange-before-ngmodel). -->
                <input [ngModel]="recoveryCode()" (ngModelChange)="recoveryCode.set($event)"
                       autocomplete="one-time-code" id="recovery-code" name="recovery-code" required
                       type="text" spellcheck="false" autocapitalize="none"
                       class="block w-full rounded-md border-2 border-gray-200 px-2 py-1.5 font-mono text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-theme-primary-700 sm:text-sm sm:leading-6">
              </div>
            </div>
            <button (click)="verifySecondFactor(recoveryCode())" type="button"
                    [disabled]="verifying() || !recoveryCode().trim()"
                    class="flex w-full justify-center rounded-md bg-theme-primary-700 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-theme-primary-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-theme-primary-700 disabled:pointer-events-none disabled:bg-gray-300 disabled:text-gray-600">
              Bestätigen
            </button>
          } @else {
            <div class="flex justify-center">
              <ngx-otp-input [options]="otpOptions" [status]="otpStatus()" [disabled]="verifying()"
                             (otpComplete)="verifySecondFactor($event)" class="text-sm"/>
            </div>
          }

          <div class="flex flex-col gap-2 text-sm sm:flex-row sm:items-center sm:justify-between">
            <!-- Offered only when the server said the account still HAS unused codes: a route that answers
                 every entry with "falsch" reads as "my printout is wrong" rather than "there is nothing here". -->
            @if (challenge()?.recoveryCodesAvailable) {
              <button (click)="toggleRecoveryCode(!usingRecoveryCode())" type="button"
                      class="text-theme-primary-700 hover:text-theme-primary-900 text-left">
                {{ usingRecoveryCode() ? 'Stattdessen Code aus der App verwenden' : 'Wiederherstellungscode verwenden' }}
              </button>
            }
            <button (click)="backToCredentials()" type="button"
                    class="text-left text-gray-500 hover:text-gray-700 sm:ml-auto">
              Zurück zur Anmeldung
            </button>
          </div>
        </div>
      } @else if (!UserSelectorService.hasUsers() || showLogin) {
        <form action="#" class="space-y-6 mx-6 py-12 sm:mx-12"
              method="POST">
          @if (UserSelectorService.hasUsers()) {
            <a (click)="showLogin = false" class="inline-flex w-full -ml-5">
              <svg class="h-5 w-5 place-self-center" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M20.834 8.037L9.64 14.5c-1.43.824-1.43 2.175 0 3l11.194 6.463c1.43.826 2.598.15 2.598-1.5V9.537c0-1.65-1.17-2.326-2.598-1.5"/>
              </svg>
              <span class="place-self-center">Accounts anzeigen</span>
            </a>
          }
          <!--
            TWO forms, not one field the server disambiguates (#279). The default is an e-mail + password
            account; the legacy login — an employee by e-mail + Firmen-Kürzel or benutzername@firmen-kürzel, or a
            management user by name — is a click away and clearly named. The point is that somebody can tell
            which KIND of account they hold, instead of finding out by which spelling happens to work.
          -->
          @if (mode() === 'native') {
            <div>
              <label class="block text-sm font-medium leading-6 text-gray-900" for="native-email">E-Mail-Adresse</label>
              <div class="mt-2">
                <input [(ngModel)]="nativeEmail" autocomplete="username" inputmode="email"
                       class="block w-full rounded-md border-2 border-gray-200 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-theme-primary-700 sm:text-sm sm:leading-6"
                       id="native-email" name="native-email" required type="email">
              </div>
            </div>

            <div class="flex flex-column">
              <label class="block text-sm font-medium leading-6 text-gray-900" for="native-password">Passwort</label>
              <div class="mt-2">
                <input [(ngModel)]="nativePassword" autocomplete="current-password"
                       class="block px-2 w-full rounded-md border-2 border-gray-200 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-theme-primary-700 sm:text-sm sm:leading-6"
                       id="native-password" name="native-password" required type="password">
              </div>
              <!-- Its own link, carrying `kind=account`, rather than the legacy one below (#279). The two forms
                   address two credential stores, and a shared link would send a CheqMe-account holder at the
                   employee flow — which answers 200, shows a reassuring banner and sends nothing. Each form
                   owning its own link is the same reason neither form falls back to the other's endpoint. -->
              <a [routerLink]="['/selfservice/forgot-password']" [queryParams]="{kind: 'account'}"
                 class="ml-auto mt-2 text-theme-primary-700 hover:text-theme-primary-900 cursor-pointer">Passwort
                vergessen?</a>
            </div>

            @if (nativeError(); as message) {
              <p role="alert" class="rounded-md bg-red-50 p-3 text-sm text-red-700">{{ message }}</p>
            }
          } @else {
            <div>
              <label class="block text-sm font-medium leading-6 text-gray-900" for="username">Benutzername</label>
              <div class="mt-2">
                <input [(ngModel)]="username"
                       class="block w-full rounded-md border-2 border-gray-200 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-theme-primary-700 sm:text-sm sm:leading-6"
                       id="username" name="username" required
                       type="text">
              </div>
              <!-- Both spellings stay supported, and neither is guessable — so the form says them rather than
                   letting somebody discover the working one by trial. -->
              <p class="mt-1 text-xs text-gray-500">
                Ihre E-Mail-Adresse, <span class="font-semibold">benutzername&#64;firmen-kürzel</span> oder Ihr
                Verwaltungs-Benutzername.
              </p>
            </div>

            <div class="flex flex-column">
              <label class="block text-sm font-medium leading-6 text-gray-900" for="password">Passwort</label>
              <div class="mt-2">
                <input [(ngModel)]="password" autocomplete="current-password"
                       class="block px-2 w-full rounded-md border-2 border-gray-200 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-theme-primary-700 sm:text-sm sm:leading-6"
                       id="password" name="password"
                       required
                       type="password">
              </div>
              <a [routerLink]="['/selfservice/forgot-password']"
                 class="ml-auto mt-2 text-theme-primary-700 hover:text-theme-primary-900 cursor-pointer">Passwort
                vergessen?</a>
            </div>
          }

          <div>
          <span class="text-sm">
            Mit dem Login bestätige ich, dass ich die
            <a [href]="location.protocol + '//' + location.host + '/static/privacy.html'"
               class="font-bold text-theme-primary-700 hover:text-theme-primary-900 cursor-pointer" target="_blank">Datenschutzbestimmungen</a>
            gelesen und verstanden habe und akzeptiere diese.
          </span>
          </div>

          <div>
            @if (mode() === 'native') {
              <button (click)="loginWithPassword()" [disabled]="signingIn()"
                      class="flex w-full justify-center rounded-md bg-theme-primary-700 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-theme-primary-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-theme-primary-700 disabled:pointer-events-none disabled:bg-gray-300 disabled:text-gray-600 login-button"
                      type="submit">
                Anmelden
              </button>
            } @else {
              <button (click)="login(username, password)"
                      class="flex w-full justify-center rounded-md bg-theme-primary-700 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-theme-primary-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-theme-primary-700 login-button"
                      type="submit">
                Anmelden
              </button>
            }
          </div>

          <!-- Shared with the invite landing (#576) — see OrDividerComponent's remarks for why a copy here
               drifted from the one there. -->
          <auth-or-divider/>

          <!-- The other account kind. A button rather than a second set of fields on the same form: two password
               boxes on one screen is the shape that makes people type the right password into the wrong one. -->
          <div>
            @if (mode() === 'native') {
              <button (click)="useMode('legacy')" type="button"
                      class="flex w-full items-center justify-center gap-2 rounded-md border-2 border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold leading-6 text-gray-700 shadow-sm hover:bg-gray-50">
                Anmeldung für bestehende Konten
              </button>
            } @else {
              <button (click)="useMode('native')" type="button"
                      class="flex w-full items-center justify-center gap-2 rounded-md border-2 border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold leading-6 text-gray-700 shadow-sm hover:bg-gray-50">
                Zurück zur Anmeldung mit E-Mail und Passwort
              </button>
            }
          </div>

          <!-- DEV-ONLY external login: only rendered where the backend exposes it (Development + CHEQME_DEV_AUTH). -->
          @if (devAuthEnabled) {
            <div class="rounded-md border-2 border-dashed border-amber-300 bg-amber-50 p-3">
              <p class="mb-2 text-xs font-semibold text-amber-700">Entwickler-Login (nur lokale Umgebung)</p>
              <div class="flex flex-col gap-2 sm:flex-row">
                <input type="email" [(ngModel)]="devEmail" name="devEmail" placeholder="E-Mail"
                       aria-label="Entwickler-Login E-Mail"
                       class="flex-1 rounded-md border-[1px] border-amber-200 px-3 py-1.5 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"/>
                <button (click)="loginWithDev()" type="button" [disabled]="!devEmail.trim()"
                        class="flex items-center justify-center gap-2 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 disabled:opacity-50">
                  Als Entwickler anmelden
                </button>
              </div>
            </div>
          }

          <!-- All three are DIRECT sign-ins (#279) — plain OIDC against the provider itself, no broker anywhere
               in the path any more. Apple 404s until CHEQME_APPLE_* is configured, which is intended: an
               unconfigured provider must look unconfigured rather than quietly falling back to a broker. -->
          <div class="space-y-2">
            <button (click)="loginWithMicrosoft()" type="button"
                    class="flex w-full items-center justify-center gap-2 rounded-md border-2 border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold leading-6 text-gray-700 shadow-sm hover:bg-gray-50">
              <svg class="h-4 w-4" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path fill="#f35325" d="M1 1h10v10H1z"/>
                <path fill="#81bc06" d="M12 1h10v10H12z"/>
                <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                <path fill="#ffba08" d="M12 12h10v10H12z"/>
              </svg>
              Mit Microsoft anmelden
            </button>

            <button (click)="loginWithApple()" type="button"
                    class="flex w-full items-center justify-center gap-2 rounded-md border-2 border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold leading-6 text-gray-700 shadow-sm hover:bg-gray-50">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"
                   aria-hidden="true">
                <path d="M17.05 12.54c-.02-2.2 1.8-3.26 1.88-3.31-1.02-1.5-2.62-1.7-3.19-1.72-1.36-.14-2.65.8-3.34.8-.69 0-1.75-.78-2.87-.76-1.48.02-2.84.86-3.6 2.18-1.53 2.66-.39 6.6 1.1 8.76.73 1.06 1.6 2.25 2.74 2.2 1.1-.04 1.52-.71 2.85-.71 1.33 0 1.7.71 2.87.69 1.18-.02 1.93-1.08 2.65-2.14.84-1.23 1.18-2.42 1.2-2.48-.03-.01-2.3-.88-2.32-3.5zM14.9 5.6c.6-.74 1.01-1.76.9-2.78-.87.04-1.93.58-2.56 1.31-.56.65-1.06 1.7-.93 2.7.97.08 1.97-.49 2.59-1.23z"/>
              </svg>
              Mit Apple anmelden
            </button>

            <button (click)="loginWithGoogle()" type="button"
                    class="flex w-full items-center justify-center gap-2 rounded-md border-2 border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold leading-6 text-gray-700 shadow-sm hover:bg-gray-50">
              <svg class="h-4 w-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path fill="#4285F4" d="M23.5 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.87c2.26-2.09 3.56-5.17 3.56-8.87z"/>
                <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.94-2.91l-3.87-3a7.2 7.2 0 0 1-10.72-3.78h-4v3.09A12 12 0 0 0 12 24z"/>
                <path fill="#FBBC05" d="M5.35 14.31a7.19 7.19 0 0 1 0-4.6v-3.1h-4a12 12 0 0 0 0 10.8l4-3.1z"/>
                <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0A12 12 0 0 0 1.35 6.62l4 3.09A7.2 7.2 0 0 1 12 4.75z"/>
              </svg>
              Mit Google anmelden
            </button>
          </div>
        </form>
      } @else {
        <div class="flex flex-col w-full relative">
          @for (user of UserSelectorService.users; track user.id) {
            <div class="w-full relative">
              <!--
                `border-[1px]`, NOT Bootstrap's `border-1` (Bug #234) — and deliberately NOT the
                obvious-looking `border`. Bootstrap's `.border-1` is `border-width: 1px !important`, i.e.
                width only, so today's divider is 1px in Tailwind's `border-gray-300`. But `.border` is ITSELF
                a Bootstrap class and a SHORTHAND
                (`border: var(--bs-border-width) var(--bs-border-style) var(--bs-border-color) !important`),
                so its `!important` covers the COLOUR too and would repaint this divider Bootstrap grey
                (#dee2e6) over the `border-gray-300` right next to it. `border-[1px]` sets the width alone and
                leaves the colour to Tailwind. Verified against
                node_modules/bootstrap/dist/css/bootstrap.min.css.
                The `!border-r-0/-l-0/-t-0` trio then zeroes three sides so only the bottom rule survives;
                those keep their `!` because they must out-rank whichever base width class is in play.
              -->
              <button
                (click)="UserSelectorService.isExpired(user.expiration) ? reauth(user) : AuthenticationService.select(user)"
                [class.sm:rounded-t-lg]="$first"
                class="w-full bg-white border-gray-300 border-[1px] !border-r-0 !border-l-0 !border-t-0 flex flex-col p-3
            hover:!bg-gray-200 focus:!z-10 hover:z-10 ring-inset">
                <div class="flex w-full">
                  <span class="fw-bold">{{ user.name }}</span>
                  <span class="ml-auto font-light text-gray-400">
                {{ UserSelectorService.isExpired(user.expiration) ? "abgemeldet" : "angemeldet" }}
              </span>
                </div>
                <div class="w-full flex flex-col text-left text-gray-400">
                  @if (user.shard) {
                    <span class="fw-light text-sm">{{ user.shard.name }}</span>
                  }
                  @if (user.company) {
                    <span class="fw-light text-sm">{{ user.company.name }}</span>
                  }
                </div>
              </button>
              <!-- #234: this carried `height-6 width-6` — names in NEITHER framework, so they emitted nothing.
                   That is the bug's other half: a Bootstrap-shaped name applies something you didn't ask for, a
                   misspelt Tailwind one applies nothing, and both are invisible in review. The button was sized
                   only by the 24px `<svg>` it wraps, so `h-6 w-6` states the intent at the same rendered size
                   (Preflight zeroes button padding, and box-sizing is border-box). -->
              <button (click)="UserSelectorService.remove(user.id)"
                      class="absolute right-4 bottom-5 h-6 w-6 text-red-600 hover:text-red-800">
                <svg class="h-6 w-6" viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M208.49 191.51a12 12 0 0 1-17 17L128 145l-63.51 63.49a12 12 0 0 1-17-17L111 128L47.51 64.49a12 12 0 0 1 17-17L128 111l63.51-63.52a12 12 0 0 1 17 17L145 128Z"/>
                </svg>
              </button>
            </div>
          }
          <div class="flex w-full py-3">
            <a (click)="showLogin = true" class="text-theme-primary-700 hover:text-theme-primary-900 m-auto">Anderen
              Account nutzen</a>
          </div>
        </div>
      }

    </div>
  </div>
</div>
